import { Authorization } from "../Authorization";
import { CockpitUser, CrmUser, MekUser } from "../api";
import { fetchUserProfile } from "../userProfile";
import Keycloak, { KeycloakLoginOptions, KeycloakProfile } from "keycloak-js";
import { KeycloakClientProvider } from "./keycloak-client";
import { loadStoredOrFetchConfiguration } from "@newsenec/configuration-provider";
import { isHostControl } from "../urlUtils";
import { isCockpitItaly } from "../auth-provider";

enum IdpHint {
    /** Azure Active Directory */
    AAD = "aad",
    /** MyEnergyKey */
    MEK = "mek",
}

export class KeycloakAuthorization implements Authorization {
    private mekUserCache: MekUser | null = null;
    private crmUserCache: CrmUser | null = null;

    private redirectUrl: string;

    constructor(private clientProvider: KeycloakClientProvider) {
        this.redirectUrl = window.location.href;
    }

    private static extractUser = (
        userProfile: KeycloakProfile,
        userInfo: object,
        resourceAccess: object | undefined,
        clientId: string | undefined,
    ): MekUser => {
        const devNumber = KeycloakAuthorization.extractDevNumber(userInfo);
        const market = KeycloakAuthorization.extractStringClaimFromToken(
            "country_code",
            userInfo as object & Record<string, string | undefined>,
        );
        const language = KeycloakAuthorization.extractStringClaimFromToken(
            "language",
            userInfo as object & Record<string, string | undefined>,
        );
        const keycloakRoles = KeycloakAuthorization.extractClientRole(resourceAccess, clientId);

        return {
            devNumber,
            keycloakRoles,
            email: userProfile.email,
            email_verified: userProfile.emailVerified,
            name: userProfile.email, // cannot use username, because it`s an uuid
            given_name: userProfile.firstName,
            family_name: userProfile.lastName,
            "https://login.ciam.com/identity_v2": {
                identityId: "",
                businessSystemReferences: [{ businessSystemCode: "senec-meinSENEC" }],
            },
            fromKeycloak: true,
            market,
            language,
        };
    };

    private static extractDevNumber = (userInfo: object): string | undefined => {
        if ("meinsenec-devnumber" in userInfo && typeof userInfo["meinsenec-devnumber"] === "string") {
            return userInfo["meinsenec-devnumber"];
        }
        return undefined;
    };

    private static extractClientRole = (resourceAccess?: object, clientId?: string): string[] | undefined => {
        if (
            resourceAccess !== undefined &&
            clientId !== undefined &&
            clientId in resourceAccess &&
            typeof resourceAccess[clientId as keyof typeof resourceAccess] === "object" &&
            "roles" in resourceAccess[clientId as keyof typeof resourceAccess] &&
            Array.isArray(resourceAccess[clientId as keyof typeof resourceAccess]["roles"])
        ) {
            return resourceAccess[clientId as keyof typeof resourceAccess]["roles"] as string[];
        }
        return undefined;
    };

    private static extractStringClaimFromToken = (
        claim: string,
        userInfo?: object & Record<string, string | undefined>,
    ): string | undefined => {
        if (userInfo !== undefined && claim in userInfo && typeof userInfo[claim] === "string") {
            return userInfo[claim];
        }
        return undefined;
    };

    /**
     * Retrieves an access token.
     * If the client has a valid token, it checks if the token is expired and refreshes it if necessary.
     */
    getAccessToken = async (): Promise<string> => {
        if (!this.clientProvider.isInitialized()) throw new Error("Authorization client not initialized.");
        const client = await this.clientProvider.getInitializedClient();
        if (client.refreshTokenParsed?.exp) {
            console.debug(`Refresh Token expires in ${client.refreshTokenParsed.exp * 1000 - Date.now()} ms`);
            if (client.refreshTokenParsed.exp * 1000 < Date.now()) {
                console.error("Refresh token is expired");
                this.logout();
                return Promise.reject(new Error("Refresh token is expired"));
            }
        }
        await client
            .updateToken(60)
            .then((refreshed) => {
                if (refreshed) {
                    console.log("Token successfully refreshed");
                } else {
                    console.debug("Token is still valid, no need to refresh.");
                }
            })
            .catch(() => {
                console.error("Failed to refresh the token, or the session has expired");
                this.logout();
            });
        if (client.token) {
            return client.token;
        } else {
            return Promise.reject(new Error("No token available."));
        }
    };

    getCrmUser = async (token: string): Promise<CrmUser> => {
        if (this.crmUserCache) {
            return this.crmUserCache;
        }
        const user = await fetchUserProfile(token);
        this.crmUserCache = user;
        return user;
    };

    getCockpitUser = async (): Promise<CockpitUser> => {
        const token = await this.getAccessToken();
        const mekUser = await this.getUser();
        const crmUser = await this.getCrmUser(token);
        return { mekUser, crmUser };
    };

    getUser = async (): Promise<MekUser | undefined> => {
        if (this.mekUserCache) {
            return this.mekUserCache;
        }

        if (!this.clientProvider.isInitialized()) throw new Error("Authorization client not initialized.");

        const client: Keycloak = await this.clientProvider.getInitializedClient();
        const userProfile = await client.loadUserProfile();
        const userInfo = await client.loadUserInfo();
        this.mekUserCache = KeycloakAuthorization.extractUser(userProfile, userInfo, client.resourceAccess, client.clientId);

        return this.mekUserCache;
    };

    loginSilently = async (): Promise<MekUser | undefined> => {
        const client = await this.clientProvider.getInitializedClient();
        this.redirectUrl = window.location.href;
        return client.authenticated ? this.getUser() : Promise.reject(new Error("User could not be authenticated."));
    };

    /**
     * Runs the login process with a full page redirect to the Keycloak server, and afterward redirects back to the
     * currently shown page. If the application is a control app and runs in production environment it uses the Azure
     * Active Directory (AAD) as identity provider (IdP).
     */
    loginWithRedirect = async (): Promise<void> => {
        const client = await this.clientProvider.getInitializedClient();
        const config = await loadStoredOrFetchConfiguration();

        const options: KeycloakLoginOptions = { redirectUri: this.redirectUrl };

        if (config.environment === "prod" && isHostControl()) {
            options.idpHint = IdpHint.AAD;
        } else if (isCockpitItaly()) {
            options.idpHint = undefined;
            options.prompt = "login";
        } else if (!isHostControl()) {
            if (config.environment === "prod" || config.environment === "pg") {
                options.idpHint = IdpHint.MEK;
            }
            options.prompt = "login"; // always show login screen when logging in again
        }
        options.locale = isCockpitItaly() ? "it" : "de";

        await client.login(options);
    };

    logout = async (url?: string): Promise<void> => {
        this.clearCache();
        const client = await this.clientProvider.getInitializedClient();
        client.createLogoutUrl;
        const locale = isCockpitItaly() ? "it" : "de";
        const redirectUri = `${url || window.location.origin}?kc_locale=${locale}`;
        try {
            // call login function
            if (client.authenticated) {
                await client.logout({ redirectUri: redirectUri });
                await client.logout({ redirectUri: url || window.location.origin });
                client.clearToken();
            } else {
                // For expired sessions, redirect directly
                window.location.href = redirectUri;
            }
        } catch (error) {
            if (error instanceof Error && error.message.includes("session_expired")) {
                console.warn("Logout failed due to expired session");
            } else {
                console.error("Logout failed", error);
                throw error;
            }
        }
    };

    /**
     * Forces the retrieval of an access token and returns it as a string.
     */
    refreshToken = async (): Promise<string> => {
        const client = await this.clientProvider.getInitializedClient();
        await client.updateToken(-1);
        return this.getAccessToken();
    };

    registerWithRedirect = async (): Promise<void> => {
        const client = await this.clientProvider.getInitializedClient();
        const options: KeycloakLoginOptions = {
            redirectUri: this.redirectUrl,
            idpHint: IdpHint.MEK,
            prompt: "login",
            action: "register",
        };
        options.loginHint = options.loginHint ? `${options.loginHint}&viewsignup=true` : "viewsignup=true";
        return client.login(options);
    };

    private clearCache(): void {
        this.mekUserCache = null;
        this.crmUserCache = null;
    }
}
